import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DairyService from '~/api/services/dairy';
import i18n from '~/components/i18n';
import notify from '~/components/Notify';
import DairyQuery from '~/QueryHooks/DairyQuery';
import CreateEditLocation from './CreateEditLocation';
import TableComponent from '~/components/TableComponent';
import Layout from '~/components/Layout';
import { El as LayoutEl } from '~/components/Layout/style';
import Dialog from '~/components/Dialog';
import ShowLocation from './ShowLocation';
import { useAuth } from '~/routes/components/AuthProvider';
import { LIGHT_USER_ROLE_SLUG } from '~/Constants';
import { handleErrors } from '~/utils/helpers';

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('Name'),
    key: 'name',
    sorted: 'desc'
  },
  {
    name: i18n.t('Headquarter'),
    key: 'headquarter',
    type: 'verified',
    sorted: 'desc'
  },
  {
    name: i18n.t('Type'),
    key: 'typeLabel'
  },
  {
    name: i18n.t('Address'),
    key: 'total',
    type: 'address'
  },
  {
    name: i18n.t('Actions')
  }
];

const getActions = () => [
  {
    popupMessage: i18n.t('Show more'),
    type: 'icon',
    name: 'eye',
    size: 'large',
    actionType: 'show'
  },
  {
    popupMessage: i18n.t('Edit'),
    type: 'icon',
    name: 'pencil',
    size: 'large',
    actionType: 'edit',
    disableForRoleSlug: LIGHT_USER_ROLE_SLUG
  },
  {
    popupMessage: i18n.t('Delete'),
    type: 'icon',
    name: 'trash',
    size: 'large',
    actionType: 'delete',
    verifiedAction: 0,
    verifiedActionKey: 'headquarter',
    disableForRoleSlug: LIGHT_USER_ROLE_SLUG
  }
]

const DairyFactoryProfileLocations: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [isBusy, setIsBusy] = useState(false);
  const [locationId, setLocationId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 10,
    column: '',
    sort: ''
  });
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())
  const [showLocationId, setShowLocationId] = useState(null)
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG

  const {
    isLoading: isBusyData,
    data: locationsData,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = DairyQuery.dairyLocationsList({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  });

  useEffect(() => {
    setTableColumnHeaders(getDefaultTableColumnHeaders())
  }, [t])

  useEffect(() => {
    fetchFilteredData();
  }, [dataFiltered, t]);

  function handleActionClick(row, type) {
    const { id } = row
    switch (type) {
      case 'show':
        return setShowLocationId(id)
      case 'edit':
        return setLocationId(id)
      case 'delete':
        return setDeleteId(id)
      default:
        return
    }
  }

  function renderBoxContent() {
    const actions = getActions()
    return <TableComponent
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      isFetchingData={isFetchingData}
      animated
      rows={rows}
      actions={lightUserLogged ? actions.filter(action => !action.disableForRoleSlug) : actions}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      total={filtered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
    />
  }

  async function handleDeleteLocation() {
    setIsBusy(true);
    try {
      await DairyService.deleteLocation(deleteId);
      notify(t('Successfully deleted location'), 'success');
      setDeleteId(null);
      fetchFilteredData();
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderDeleteModal() {
    return <Dialog
      isDialogOpen={Boolean(deleteId)}
      textClose={t('No')}
      isLoading={isBusy}
      handleClose={() => setDeleteId(null)}
      textSubmit={t('Yes')}
      handleSubmit={() => handleDeleteLocation()}
      textContent={t('Are you sure you want to delete location')}
      textHeader={t('Delete location')}
      type={'abort'}
    />
  }

  function renderActions() {
    return <LayoutEl.PageHeaderActionButton
      onClick={() => setLocationId('create')}
    >{t('Add New Location')}</LayoutEl.PageHeaderActionButton>
  }

  const { filtered, rows } = locationsData ? locationsData : { filtered: 0, rows: [] };

  return (
    <Layout
      isBusy={isBusyData}
      title={t('Locations')}
      childContainerSize={'auto'}
      fontSizeHeader={'18px'}
      useSpinner
      actions={lightUserLogged ? null : renderActions()}
    >
      {!lightUserLogged && <>
        {deleteId && renderDeleteModal()}
        <CreateEditLocation
          isVisible={Boolean(locationId)}
          locationId={locationId}
          handleHiddenDrawer={() => {
            setLocationId(null);
            fetchFilteredData();
          }}
        />
      </>
      }
      {renderBoxContent()}
      <ShowLocation
        locationId={showLocationId}
        handleHiddenDrawer={() => setShowLocationId(null)}
      />
    </Layout>
  );
};

export default DairyFactoryProfileLocations;
